/* What a disagreement on this definition is worth telling somebody — and whether anybody chose it.
 *
 * **The badge is the load-bearing part.** `declared: false` still opens an incident at `warning` on
 * the first break: the mechanism ships on rather than off, because a control that arrives switched
 * off is a control nobody notices is missing. But nobody at this tenant chose that, and a panel that
 * rendered the platform's default as somebody's decision is how a tenant comes to believe a
 * threshold was set that nobody set.
 */

import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { BreakPolicy, BreakSeverity } from '@/shared/api/generated/reconciliation';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { useBreakPolicy, useSaveBreakPolicy } from '../api/governance';
import { isPlatformDefault, policyBodyKey, policyHeadline } from '../model/governance';

const SEVERITIES: readonly BreakSeverity[] = ['info', 'warning', 'critical'];

export function BreakPolicyPanel({
  definitionId,
  canConfigure,
}: {
  readonly definitionId: string;
  readonly canConfigure: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const policy = useBreakPolicy(definitionId);
  const save = useSaveBreakPolicy(definitionId);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<{
    enabled: boolean;
    severity: BreakSeverity;
    minBreaks: string;
  } | null>(null);

  function beginEditing(current: BreakPolicy): void {
    setDraft({
      enabled: current.enabled,
      severity: current.severity,
      minBreaks: String(current.min_breaks),
    });
    setOpen(true);
  }

  const minBreaks = Number(draft?.minBreaks ?? '');
  /* The substrate's own floor. A threshold of zero would mean "open an incident when a run finds no
     breaks", which is not a quieter setting — it is a different and nonsensical one. */
  const minProblem =
    draft !== null && (!Number.isInteger(minBreaks) || minBreaks < 1)
      ? t('reconciliation.policy.error.min')
      : null;

  return (
    <section className="lq-card lq-rc__panel">
      <div className="lq-rc__panelHead">
        <h3 className="lq-rc__panelTitle">{t('reconciliation.policy.title')}</h3>
        {canConfigure ? (
          policy.isSuccess && (
            <button
              type="button"
              className="lq-linkbutton"
              onClick={() => {
                if (open) setOpen(false);
                else beginEditing(policy.data);
              }}
            >
              {t(
                open ? 'reconciliation.policy.close' : 'reconciliation.policy.configure',
              )}
            </button>
          )
        ) : (
          /* Withheld rather than absent, and it says which. An impossible action and an
             unpermitted one must not look alike. */
          <span className="lq-muted" title={t('reconciliation.policy.gated.why')}>
            {t('reconciliation.policy.gated')}
          </span>
        )}
      </div>

      {policy.isPending && <SkeletonRows rows={2} columns={1} />}
      {policy.isError && (
        <Alert tone="error" title={t('reconciliation.policy.failed')}>
          {policy.error.message}
        </Alert>
      )}

      {policy.isSuccess && (
        <>
          {isPlatformDefault(policy.data) ? (
            <span className="lq-rc__badge lq-rc__badge--warn">
              {t('reconciliation.policy.badge.default')}
            </span>
          ) : (
            <span className="lq-rc__badge">
              {t('reconciliation.policy.badge.declared', {
                who: policy.data.configured_by ?? '',
                when:
                  policy.data.configured_at === null
                    ? ''
                    : new Date(policy.data.configured_at).toLocaleDateString(),
              })}
            </span>
          )}

          <p className="lq-rc__headline">
            {(() => {
              const reading = policyHeadline(policy.data);
              return t(reading.key, {
                ...reading.values,
                severity: t(`reconciliation.severity.${policy.data.severity}`),
              });
            })()}
          </p>
          <p className="lq-rc__panelBody">
            {t(policyBodyKey(policy.data), { count: policy.data.min_breaks })}
          </p>
          <p className="lq-note">{t('reconciliation.policy.whyWarning')}</p>

          {open && draft !== null && (
            <div className="lq-rc__form">
              <label className="lq-rc__check">
                <input
                  type="checkbox"
                  checked={draft.enabled}
                  onChange={(event) => {
                    setDraft({ ...draft, enabled: event.target.checked });
                  }}
                />
                <strong>{t('reconciliation.policy.enabled')}</strong>
              </label>

              <div className="lq-rc__formLabel">
                {t('reconciliation.policy.severity')}
              </div>
              <div className="lq-rc__choices">
                {SEVERITIES.map((severity) => (
                  <label
                    key={severity}
                    className={`lq-rc__choice${draft.severity === severity ? ' lq-rc__choice--on' : ''}`}
                  >
                    <input
                      type="radio"
                      name="reconciliation-severity"
                      checked={draft.severity === severity}
                      onChange={() => {
                        setDraft({ ...draft, severity });
                      }}
                    />
                    {t(`reconciliation.severity.${severity}`)}
                  </label>
                ))}
              </div>

              <div className="lq-rc__formLabel">
                {t('reconciliation.policy.minBreaks')}
              </div>
              <input
                type="number"
                min={1}
                className="lq-rc__number"
                value={draft.minBreaks}
                onChange={(event) => {
                  setDraft({ ...draft, minBreaks: event.target.value });
                }}
              />
              <p className="lq-rc__panelBody">
                {t('reconciliation.policy.minBreaks.note')}
              </p>

              {minProblem !== null && <p className="lq-rc__error">{minProblem}</p>}
              {save.isError && <p className="lq-rc__error">{save.error.message}</p>}

              <Button
                tone="primary"
                onClick={() => {
                  if (minProblem !== null) return;
                  save.mutate(
                    {
                      enabled: draft.enabled,
                      severity: draft.severity,
                      min_breaks: minBreaks,
                    },
                    {
                      onSuccess: () => {
                        setOpen(false);
                      },
                    },
                  );
                }}
                disabled={minProblem !== null || save.isPending}
              >
                {t('reconciliation.policy.save')}
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
